import './RoleStageActions.scss';
import Loader from 'components/atoms/Loader/Loader';
import Notification from 'components/atoms/Notification/Notification';
import AppBar from 'components/organisms/AppBar/AppBar';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import ExpirationNotification from '@/components/organisms/ExpirationNotification/ExpirationNotification';
import { HEAP_ANALYTICS_EVENTS } from '@/constants/analytics';
import { appSwitchRoutes, getRoleHomeUrl } from 'constants/routes';
import { candidateEmail } from 'constants/candidateEmail';
import { FormType } from 'enums/form';
import { ModalTypes } from 'enums/modalTypes';
import { StageActionAvailabilityStatus } from 'enums/stageAction';
import { AssessmentStageKind, useMyRoleStageActionsQuery } from 'generated/graphql';
import { closeModal, openModal } from 'graphql/modals';
import { useGetStageCompanyRespondantForm } from 'hooks/useRespondantForm';
import { FC, MouseEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  analyticsClient,
  NavLink,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import AnticheatingContract from './AnticheatingContract/AnticheatingContract';
import { parseApiDeadline } from 'helpers/deadline';
import {
  getCompanyLogoUrl,
  getPortalBrandingStyle,
} from '../CandidatePortal/CandidatePortal.helpers';
import StageActionCard from './StageActionCard';
import { isStageActionable } from './flows/useRoleStageActionsFlow';
import {
  parseRoleActionCards,
  TRoleActionCardData,
} from './RoleStageActions.helpers';

const stageKindDisplayName: Record<AssessmentStageKind, string> = {
  [AssessmentStageKind.Assessment]: 'stage.stageKind.assessment',
  [AssessmentStageKind.AiInterview]: 'stage.stageKind.aiInterview',
};

const RoleStageActions: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleMsgType } = useNotification();
  const { roleId, stageId } = useParams();

  const myRoleStageActionsQueryResponse = useMyRoleStageActionsQuery({
    variables: {
      roleId: roleId as string,
      stageId: stageId as string,
    },
    skip: !roleId || !stageId,
    onError: error => {
      // Backend identity-verification gate: divert into the verification flow
      // and resume this stage once approved.
      const needsIdVerification = error.graphQLErrors?.some(gqlError =>
        gqlError.message.includes('IDENTITY_VERIFICATION_REQUIRED')
      );
      if (needsIdVerification) {
        navigate(
          appSwitchRoutes.roleStageIdentityVerification.getUrl(
            roleId as string,
            stageId as string
          ),
          { state: { from: location.pathname } }
        );
        return;
      }

      handleMsgType({
        type: TNotification.error,
        message: error?.message,
      });
    },
    fetchPolicy: 'network-only',
  });

  const role = myRoleStageActionsQueryResponse.data?.myRole;
  const stage = role?.stages[0];
  const backUrl = getRoleHomeUrl(
    roleId as string,
    role?.candidatePortalEnabled
  );

  const getStageCompanyRespondantForm = useGetStageCompanyRespondantForm();

  // The page owns the reasonable-adjustments gate: divert into the (legacy)
  // adjustments flow before the candidate can see the stage actions.
  const needsAdjustmentQuestionnaire =
    !!stage &&
    stage.hasAdjustmentQuestionnaire &&
    !stage.hasCompletedAdjustmentQuestionnaire;

  // Once the stage candidate is no longer actionable (completed, processing,
  // signed off, ...) there is nothing left to do here — back to the stage list.
  const stageNoLongerActionable = !!stage && !isStageActionable(stage);

  // The candidate's own deadline, as quoted in their invite email. Shown in
  // the "complete by" notice below; it does not close the stage.
  const stageDeadline = parseApiDeadline(stage?.deadlineAt);

  useEffect(() => {
    if (stageNoLongerActionable) {
      navigate(backUrl);
      return;
    }
    if (needsAdjustmentQuestionnaire && stage) {
      navigate(
        appSwitchRoutes.roleStageAdjustments.getUrl(
          roleId as string,
          stageId as string,
          'intro'
        )
      );
    }
  }, [
    stageNoLongerActionable,
    backUrl,
    needsAdjustmentQuestionnaire,
    stage,
    navigate,
    roleId,
    stageId,
  ]);

  // Mirrors the legacy StageView cleanup so a pending candidate-information
  // modal doesn't outlive the page.
  useEffect(() => {
    return () => closeModal(ModalTypes.CANDIDATE_INFORMATION);
  }, []);

  /** Test cards are gated behind the candidate-information form: incomplete
   * CI form opens the modal instead of starting the test (legacy
   * StageActionTestCard behaviour). */
  const handleTestCardClick =
    (actionCard: TRoleActionCardData, stageCandidateId: number) =>
    async (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      const isCardAvailable =
        actionCard.availability === StageActionAvailabilityStatus.Available;
      try {
        const candidateInformationRespondantForm =
          await getStageCompanyRespondantForm(
            stageCandidateId,
            FormType.CI_FORM
          );

        if (
          candidateInformationRespondantForm?.data?.respondantForm &&
          candidateInformationRespondantForm.data.respondantForm
            .respondantFieldWithAnswers.length &&
          !candidateInformationRespondantForm.data.respondantForm.isComplete
        ) {
          openModal({
            modalType: ModalTypes.CANDIDATE_INFORMATION,
            modalProps: {
              stageCandidateId,
              companyLogoUrl: role?.company.logoUrl,
            },
          });
        } else if (isCardAvailable) {
          navigate(actionCard.linkTo);
        }
      } catch (_error) {
        if (isCardAvailable) {
          navigate(actionCard.linkTo);
        }
      }
    };

  const handleDefaultCardClick =
    (actionCard: TRoleActionCardData) =>
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      if (
        actionCard.availability === StageActionAvailabilityStatus.Available
      ) {
        navigate(actionCard.linkTo);
      }
    };

  /** AI interview cards stay plain links (the conference page is a separate
   * app shell); mirror the legacy card's analytics on click. The numeric
   * stage id isn't exposed on MyRoleStage, so the event carries the stage
   * candidate id only. */
  const handleAiInterviewCardClick =
    (actionCard: TRoleActionCardData, stageCandidateId: number) =>
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (
        actionCard.availability !== StageActionAvailabilityStatus.Available
      ) {
        event.preventDefault();
        return;
      }

      analyticsClient.addEventProperties({
        stageCandidateId,
        conferenceId: actionCard.aiInterview?.conferenceId,
        aiInterviewAssessmentStageActionCandidateId:
          actionCard.aiInterview?.actionCandidateId,
      });

      analyticsClient.trackEvent({
        name: HEAP_ANALYTICS_EVENTS.CANDIDATE_STAGE_AI_INTERVIEW_STAGE_CARD_CLICK,
      });
    };

  return (
    <>
      <HelmetAndPageAnnouncer
        pageTitle={role?.name || t('roles.roleDetails')}
      />
      <div
        className="RoleStageActions"
        aria-live="polite"
        // Whitelabel colours: primary buttons on this page pick these up via
        // the design-system button's --button-primary-bg* hooks.
        style={getPortalBrandingStyle(role?.company.branding)}
      >
        <AppBar />
        <nav className="RoleStageActions__Nav">
          <NavLink
            to={backUrl}
            onClick={e => {
              e.preventDefault();
              navigate(backUrl);
            }}
            aria-label={t('roles.backToStageList')}
            direction="backward"
            className="RoleStageActions__Nav__BackButton"
          >
            {t('roles.stageList')}
          </NavLink>
          {/* The candidate's own deadline wins over the stage-wide end time. */}
          {stageDeadline || stage?.endTime ? (
            <ExpirationNotification
              endTime={stageDeadline ?? new Date(stage?.endTime)}
            />
          ) : null}
        </nav>
        <main>
          {myRoleStageActionsQueryResponse.loading ? (
            <Loader isOverlay />
          ) : myRoleStageActionsQueryResponse.error || !role || !stage ? (
            <div className="RoleStageActions__NotificationWrapper">
              <Notification
                notification={{
                  icon: 'Warning',
                  color: 'Purple',
                  message: t(
                    'roles.unfortunatelyThereHasBeenProblemLoadingRoles',
                    {
                      candidateEmail,
                    }
                  ),
                }}
              />
            </div>
          ) : (
            <>
              <div className="RoleStageActions__Header">
                {getCompanyLogoUrl(role.company) ? (
                  <img
                    className="RoleStageActions__Header__CompanyLogo"
                    src={getCompanyLogoUrl(role.company)}
                    alt={t('alt.companyLogo', {
                      companyName: role.company.name,
                    })}
                  />
                ) : null}
                <h1 className="RoleStageActions__Header__Name" tabIndex={-1}>
                  {role.name}
                </h1>
                <p className="RoleStageActions__Header__CompanyName">
                  {role.company.name}
                </p>
                <p className="RoleStageActions__Header__StageName">
                  {/* Same rule as the stages-list card: assessment stages
                      show the company-configured name (a role can have
                      several), AI interviews keep the localised kind label. */}
                  {stage.kind === AssessmentStageKind.Assessment && stage.name
                    ? stage.name
                    : t(stageKindDisplayName[stage.kind])}
                </p>
              </div>
              {!stage.hasPledgedAnticheating ? (
                <div className="RoleStageActions__Pledge">
                  <AnticheatingContract
                    stageCandidateId={stage.stageCandidateId}
                    isIntegrityMonitoringEnabled={
                      stage.isIntegrityMonitoringEnabled
                    }
                    companyName={role.company.name}
                    onPledgeSubmit={() =>
                      myRoleStageActionsQueryResponse.refetch()
                    }
                  />
                </div>
              ) : (
                <div
                  className="RoleStageActions__Actions"
                  aria-label={t('common.actionsList')}
                >
                  {parseRoleActionCards(
                    stage.actions,
                    t,
                    roleId as string,
                    stageId as string
                  ).map(actionCard => (
                    <StageActionCard
                      key={actionCard.key}
                      title={actionCard.title}
                      icon={actionCard.icon}
                      status={actionCard.availability}
                      linkTo={actionCard.linkTo}
                      onClick={
                        actionCard.kind === 'test'
                          ? handleTestCardClick(
                              actionCard,
                              stage.stageCandidateId
                            )
                          : actionCard.kind === 'aiInterview'
                            ? handleAiInterviewCardClick(
                                actionCard,
                                stage.stageCandidateId
                              )
                            : handleDefaultCardClick(actionCard)
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default RoleStageActions;
